import React, { useEffect, useState } from 'react'
import { CircularProgress, Typography, styled } from '@mui/material'
import { useDispatch } from 'react-redux'
import QRCode from 'react-qr-code'
import TextFieldForModals from '../ModalMessages/TextFieldForModals'
import { BUTTON_COLOR_NEW_UI } from '../../../../utils/ColorPicker'
import ModalConfirmButton from '../ModalMessages/ModalConfirmButton'
import { TWO_FA_SHIELD_ICON } from '../../../../utils/constants'
import { UserServiceClientApi } from '../../../../services/api/UserApiServices'
import { appType } from '../../../../utils/config/configHelpers'
import { setUpdateLoggedUserDetails, updateMFAStatus, updateQrCodeUri, updateSoftwareMfaStatus } from '../../../../state/actions/user'
import { getAuthData, storeAuthData } from '../../../../services/storage/LocalStorage'
import { showToast } from '../../toaster'
import PasswordFieldForModals from '../ModalMessages/PasswordFieldForModals'

const CustomErrorTypography = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    color: '#F74646',
    fontFamily: 'Figtree-regular',
    textAlign: 'center',
    marginRight: '15px'
}));

const CustomizedHeadingTypography = styled(Typography)(({ theme }) => ({
    fontFamily: 'Figtree-Bold',
    fontSize: '19px',
    color: '#15194B',
    textAlign: 'center',
    marginRight: '15px'
}));

const CustomizedSubHeadingTypography = styled(Typography)(({ theme }) => ({
    fontFamily: 'Figtree-Regular',
    fontSize: '16px',
    color: '#15194B',
    textAlign: 'center',
    marginRight: '25px',
    marginTop: "10px"
}));


const TwoFactorAuthModal = ({ stepOf2FA, setStepOf2FA, close2FaModalOnSuccess }) => {

    const [userPassword, setUserPassword] = useState("")
    const [passwordToDeactivate2Fa, setPasswordToDeactive2Fa] = useState("")
    const [authOtpCode, setAuthOtpCode] = useState("")
    const [qrCodeUrl, updateQRCodeUrlState] = useState(null);
    const [showSpinner , setShowSpinner] = useState(false)

    //step 1,2,3 use to activate mfa
    const [step1Error, setStep1Error] = useState("")
    const [step3Error, setStep3Error] = useState("")

    //step4 is deactivating mfa
    const [step4Error, setStep4Error] = useState("")


    const dispatch = useDispatch()

    const handleUserPassword = (e) => {
        setUserPassword(e.target.value)
    }

    const handleAuthenticatorOTP = (e) => {
        setAuthOtpCode(e.target.value)
    }

    const onUserPasswordSubmit = async () => {
        if (userPassword) {
            await verifyPassword(userPassword)
        } else {
            setStep1Error('Please enter password')
        }
    }

    const onSubmitScanQR = () => {
        setStepOf2FA(3)
    }

    const onSubmitOTP = async () => {
        if (authOtpCode) {
            await verifyOTP(authOtpCode, userPassword)
        } else {
            setStep3Error('Please enter OTP')
        }
    }

    const verifyPassword = async (password) => {
        setShowSpinner(true)
        const data = {
            appType: appType || 'frontm',
            password
        }
        try {
            const response = await UserServiceClientApi.initiateSoftwareMfa(data)

            if (response?.data?.success && response?.data?.qrCodeUri) {
                dispatch(updateSoftwareMfaStatus('validateNumber'))
                dispatch(updateQrCodeUri(response.data.qrCodeUri))
                updateQRCodeUrlState(response.data.qrCodeUri)
                setStep1Error(null)
                setStepOf2FA(2)
            }
            else {
                setStep1Error(response.data.errorMessage || 'Invalid password')
            }
        }
        catch (error) {
            setStep1Error("Something went wrong!")
        }finally{
            setShowSpinner(false)
        }
    }

    const verifyOTP = async (otpToken, password) => {
        setShowSpinner(true)
        const data = {
            appType: appType || 'frontm',
            otpToken,
            password
        }

        try {
            const response = await UserServiceClientApi.activeSoftwareMfa(data)

            if (response?.data?.success) {
                setStep3Error(null)
                const userDetails = getAuthData();
                const updatedUser = {
                    ...userDetails.user,
                    softwareMfaEnabled: true
                };
                const newAuthData = {
                    ...userDetails,
                    user: updatedUser
                };

                storeAuthData(newAuthData);
                dispatch(setUpdateLoggedUserDetails(newAuthData));
                close2FaModalOnSuccess()
                showToast("success", "Two-factor Authentication has been enabled.")
            } else {
                if (response?.data?.errorCode === 'CodeMismatchException') {
                    dispatch(updateSoftwareMfaStatus('idleValidateNumber'))
                }
                if (response?.data?.errorCode === 'NotAuthorizedException') {
                    dispatch(updateSoftwareMfaStatus('idle'))
                }
                if (response?.data?.errorMessage === 'Code mismatch') {
                    dispatch(updateSoftwareMfaStatus('idle'))
                }
                else {
                    dispatch(updateSoftwareMfaStatus('idle'))
                }
                setStep3Error(response?.data?.errorMessage || response?.data?.message || 'Something went wrong!')
            }
        }
        catch (error) {
            setStep3Error("Something went wrong!")
        }finally{
            setShowSpinner(false)
        }
    }

    const handlePasswordToDeactivate2fa = (e) => {
        setPasswordToDeactive2Fa(e.target.value)
    }

    const deactivate2fa = async () => {
        if (passwordToDeactivate2Fa) {
            const dataObj = {
                appType: appType || 'frontm',
                password: passwordToDeactivate2Fa,
            }
            setShowSpinner(true)
            try {
                const response = await UserServiceClientApi.deactivateSoftwareMfa(dataObj)
                if (response?.data?.success) {
                    let userDetails = getAuthData()
                    userDetails.user.softwareMfaEnabled = false
                    storeAuthData(userDetails)
                    dispatch(updateMFAStatus(null))
                    dispatch(updateSoftwareMfaStatus(null))
                    setStep4Error(null)
                    close2FaModalOnSuccess()
                    showToast("success", "Two-factor Authentication has been deactivated.")
                } else {
                    setStep4Error(
                        response?.data?.errorMessage ||
                        response?.data?.message ||
                        "Incorrect username or password."
                    );
                }
            }
            catch (error) {
                setStep4Error('Something went wrong!')
            }finally{
                setShowSpinner(false)
            }

        } else {
            setStep4Error('Please enter password')
        }
    }

    return (
        <>
            {
                stepOf2FA === 1 && (
                    <>
                        {
                            step1Error && (
                                <CustomErrorTypography>
                                    {step1Error}
                                </CustomErrorTypography>
                            )
                        }
                        <PasswordFieldForModals
                            value={userPassword}
                            label="Password"
                            placeholder="Please enter password"
                            onChangeFn={(e) => handleUserPassword(e)}
                            customMarginTop="35px"
                            type="password"
                        />

                        <ModalConfirmButton
                            customWidth={400}
                            customBgColor={BUTTON_COLOR_NEW_UI}
                            // label="Continue"
                            label={
                                showSpinner ? (
                                    <CircularProgress size='2vh' thickness={5} color='inherit' />
                                ) : (
                                    "Continue"
                                )
                            }
                            customMarginTop="30px"
                            custompadding={"10px 0px"}
                            onClickFn={onUserPasswordSubmit}
                             disabled={showSpinner}
                        />
                    </>
                )
            }

            {
                stepOf2FA === 2 && (
                    <>
                        <div className='shieldImage2FA'>
                            <img loading="lazy" src={TWO_FA_SHIELD_ICON} width={"70px"} alt="2faShieldcon" />
                        </div>

                        <CustomizedHeadingTypography>
                            <b>
                            Add extra security with two-factor authentication
                            </b>
                        </CustomizedHeadingTypography>

                        <div className='qrImage2FA'>
                            <QRCode value={qrCodeUrl} />
                        </div>

                        <CustomizedSubHeadingTypography>
                            Scan the above QR code in your authentication app
                        </CustomizedSubHeadingTypography>

                        <ModalConfirmButton
                            customWidth={450}
                            customBgColor={BUTTON_COLOR_NEW_UI}
                            // label="Continue"
                             label={
                                showSpinner ? (
                                    <CircularProgress size='2vh' thickness={5} color='inherit' />
                                ) : (
                                    "Continue"
                                )
                            }
                            customMarginTop="30px"
                            custompadding={"10px 0px"}
                            onClickFn={onSubmitScanQR}
                             disabled={showSpinner}
                        />
                    </>
                )
            }

            {
                stepOf2FA === 3 && (
                    <>
                        <div className='shieldImage2FA'>
                            <img loading="lazy" src={TWO_FA_SHIELD_ICON} width={"70px"} alt="2faShieldcon" />
                        </div>

                        <CustomizedHeadingTypography>
                            <b>
                            Authenticate your account to access
                            </b>
                        </CustomizedHeadingTypography>

                        <CustomizedSubHeadingTypography>
                            A validation code has been sent to your authenticator app
                        </CustomizedSubHeadingTypography>

                        {step3Error && (
                            <CustomErrorTypography>
                                {step3Error}
                            </CustomErrorTypography>
                        )}

                        <TextFieldForModals
                            value={authOtpCode}
                            label="Validate OTP"
                            placeholder="Please enter OTP"
                            onChangeFn={(e) => handleAuthenticatorOTP(e)}
                            customMarginTop="35px"
                            type="number"
                            customMarginLeft='15px'
                        />

                        <ModalConfirmButton
                            customWidth={400}
                            customBgColor={BUTTON_COLOR_NEW_UI}
                            // label="Submit"
                             label={
                                showSpinner ? (
                                    <CircularProgress size='2vh' thickness={5} color='inherit' />
                                ) : (
                                    "Submit"
                                )
                            }
                            customMarginTop="30px"
                            custompadding={"10px 0px"}
                            onClickFn={onSubmitOTP}
                            customMarginLeft='15px'
                             disabled={showSpinner}
                        />
                    </>
                )
            }

            {
                stepOf2FA === 4 && (
                    <>
                        {
                            step4Error && (
                                <CustomErrorTypography>
                                    {step4Error}
                                </CustomErrorTypography>
                            )
                        }
                        <PasswordFieldForModals
                            value={passwordToDeactivate2Fa}
                            label="Password"
                            placeholder="Please enter password"
                            onChangeFn={(e) => handlePasswordToDeactivate2fa(e)}
                            customMarginTop="35px"
                            type="password"
                        />

                        <ModalConfirmButton
                            customWidth={400}
                            customBgColor={BUTTON_COLOR_NEW_UI}
                            // label="Submit"
                             label={
                                showSpinner ? (
                                    <CircularProgress size='2vh' thickness={5} color='inherit' />
                                ) : (
                                    "Submit"
                                )
                            }
                            customMarginTop="30px"
                            custompadding={"10px 0px"}
                            onClickFn={deactivate2fa}
                            disabled={showSpinner}
                        />
                    </>
                )
            }
        </>
    )
}

export default TwoFactorAuthModal